import React from "react";
import Typography from "aldoo-ra/Typography";
import FieldSelector from "./components/FieldSelector";
import DropDownSelect from "aldoo-ra/DropdownSelect";

/**
 * Field Visualizer Plugin
 * Visualizes field values across selected items
 */
export default {
  id: "cms-visualizer",
  name: "Field Visualizer",
  description: "Visualize field values across items",
  category: "analysis",

  requiredCapabilities: {
    needsContentType: true,
    needsSelection: true,
    needsFieldAccess: true,
    needsTypeModification: false,
  },

  validate(context) {
    const errors = [];

    if (!context.contentType) {
      errors.push({ message: "Content type is required" });
    }

    if (!context.selectedItems || context.selectedItems.length === 0) {
      errors.push({ message: "Please select items to visualize" });
    }

    return {
      valid: errors.length === 0,
      errors: errors.length > 0 ? errors : undefined,
    };
  },

  getInterface(context) {
    const fields = context.typeUtils.getFieldsWithTypes();

    return {
      dialog: {
        title: "Visualize Field Data",
        width: "90%",
        height: "80vh",
        hideExecuteButton: true,
        layout: "horizontal", // Side-by-side layout
        fields: [
          {
            id: "field",
            type: "custom",
            label: "Select Field to Visualize",
            required: true,
            render: (props) => (
              <FieldSelector
                value={props.value}
                onChange={props.onChange}
                fields={fields}
                multiple={false}
                label="Select Field"
                required={true}
                error={props.error}
              />
            ),
          },
          {
            id: "viewType",
            type: "custom",
            label: "View Type",
            defaultValue: "list",
            render: (props) => {
              const options = [
                { value: "list", label: "List View", name: "List View" },
                { value: "gallery", label: "Gallery View", name: "Gallery View" },
                { value: "table", label: "Table View", name: "Table View" },
                { value: "stats", label: "Statistics View", name: "Statistics View" }
              ];

              return (
                <div>
                  <Typography variant="admin_label" className="mb-1">
                    View Type
                  </Typography>
                  <DropDownSelect
                    options={options}
                    value={props.value ? [options.find(opt => opt.value === props.value)] : [options[0]]}
                    onChange={(selected) => {
                      const selectedItem = Array.isArray(selected) ? selected[0] : selected;
                      props.onChange(selectedItem?.value || "list");
                    }}
                    variant="admin"
                    nameField="name"
                    showSelectionTick={true}
                  />
                </div>
              );
            },
          },
        ],

        preview: {
          render: (values, context) => {
            if (!values.field) {
              return (
                <Typography variant="admin_caption" className="text-admin_muted">
                  Select a field to visualize
                </Typography>
              );
            }

            const fieldDef = context.typeUtils.getFieldDefinition(values.field);

            // Determine if this is a content field or root field
            const isContentField = values.field.startsWith('content.');
            const actualFieldName = isContentField ? values.field.substring(8) : values.field;

            const fieldValues = context.selectedItems.map((item) => ({
              itemId: item._id,
              itemName: item.name || item._id,
              value: isContentField ? item.content?.[actualFieldName] : item[actualFieldName],
            }));

            // List View
            if (values.viewType === "list") {
              return (
                <div className="max-h-96 overflow-y-auto">
                  <Typography variant="admin_body" className="mb-3">
                    Showing {fieldValues.length} item(s)
                  </Typography>
                  {fieldValues.map((item, index) => (
                    <div key={index} className="mb-2 p-2 bg-admin_surface rounded border border-admin_bordered">
                      <Typography variant="admin_caption" className="text-admin_muted mb-1">
                        {item.itemName}
                      </Typography>
                      <Typography variant="admin_body">
                        {item.value !== undefined && item.value !== null
                          ? typeof item.value === "object"
                            ? JSON.stringify(item.value)
                            : String(item.value)
                          : "(empty)"}
                      </Typography>
                    </div>
                  ))}
                </div>
              );
            }

            // Gallery View (for images)
            if (values.viewType === "gallery") {
              // Filter for valid image URLs (must start with http:// or https://)
              const imageItems = fieldValues.filter(
                (item) => item.value &&
                          typeof item.value === "string" &&
                          (item.value.startsWith('http://') || item.value.startsWith('https://'))
              );

              if (imageItems.length === 0) {
                return (
                  <Typography variant="admin_body" className="text-admin_muted">
                    No valid image URLs found
                  </Typography>
                );
              }

              return (
                <div className="max-h-96 overflow-y-auto">
                  <Typography variant="admin_body" className="mb-3">
                    Showing {imageItems.length} image(s)
                  </Typography>
                  <div className="grid grid-cols-3 gap-4">
                    {imageItems.map((item, index) => (
                      <div key={index} className="border border-admin_bordered rounded overflow-hidden">
                        <img
                          src={item.value}
                          alt={item.itemName}
                          className="w-full h-32 object-cover"
                        />
                        <div className="p-2 bg-admin_background">
                          <Typography variant="admin_caption" className="text-admin_muted truncate">
                            {item.itemName}
                          </Typography>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            // Table View
            if (values.viewType === "table") {
              return (
                <div className="max-h-96 overflow-auto">
                  <table className="w-full">
                    <thead className="bg-admin_surface sticky top-0">
                      <tr>
                        <th className="text-left p-2 border-b border-admin_bordered">
                          <Typography variant="admin_label">Item</Typography>
                        </th>
                        <th className="text-left p-2 border-b border-admin_bordered">
                          <Typography variant="admin_label">{fieldDef?.label || values.field}</Typography>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {fieldValues.map((item, index) => (
                        <tr key={index} className="border-b border-admin_bordered">
                          <td className="p-2">
                            <Typography variant="admin_body">{item.itemName}</Typography>
                          </td>
                          <td className="p-2">
                            <Typography variant="admin_body">
                              {item.value !== undefined && item.value !== null
                                ? typeof item.value === "object"
                                  ? JSON.stringify(item.value)
                                  : String(item.value)
                                : "(empty)"}
                            </Typography>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }

            // Statistics View
            if (values.viewType === "stats") {
              const nonEmptyValues = fieldValues.filter(
                (item) => item.value !== undefined && item.value !== null && item.value !== ""
              );
              const emptyValues = fieldValues.length - nonEmptyValues.length;

              // Count unique values
              const valueCounts = {};
              nonEmptyValues.forEach((item) => {
                const valueStr =
                  typeof item.value === "object" ? JSON.stringify(item.value) : String(item.value);
                valueCounts[valueStr] = (valueCounts[valueStr] || 0) + 1;
              });

              const sortedValues = Object.entries(valueCounts).sort((a, b) => b[1] - a[1]);

              return (
                <div className="max-h-96 overflow-y-auto">
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="p-3 bg-admin_surface rounded border border-admin_bordered">
                      <Typography variant="admin_caption" className="text-admin_muted mb-1">
                        Total Items
                      </Typography>
                      <Typography variant="admin_h2">{fieldValues.length}</Typography>
                    </div>
                    <div className="p-3 bg-admin_surface rounded border border-admin_bordered">
                      <Typography variant="admin_caption" className="text-admin_muted mb-1">
                        Populated
                      </Typography>
                      <Typography variant="admin_h2">{nonEmptyValues.length}</Typography>
                    </div>
                    <div className="p-3 bg-admin_surface rounded border border-admin_bordered">
                      <Typography variant="admin_caption" className="text-admin_muted mb-1">
                        Empty
                      </Typography>
                      <Typography variant="admin_h2">{emptyValues}</Typography>
                    </div>
                    <div className="p-3 bg-admin_surface rounded border border-admin_bordered">
                      <Typography variant="admin_caption" className="text-admin_muted mb-1">
                        Unique Values
                      </Typography>
                      <Typography variant="admin_h2">{sortedValues.length}</Typography>
                    </div>
                  </div>

                  {sortedValues.length > 0 && (
                    <div>
                      <Typography variant="admin_label" className="mb-2">
                        Top Values
                      </Typography>
                      {sortedValues.slice(0, 10).map(([value, count], index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center mb-2 p-2 bg-admin_surface rounded"
                        >
                          <Typography variant="admin_body" className="flex-1 truncate mr-2">
                            {value}
                          </Typography>
                          <Typography variant="admin_body" className="text-admin_muted">
                            {count} ({Math.round((count / fieldValues.length) * 100)}%)
                          </Typography>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return null;
          },
        },
      },
    };
  },

  async execute(context, params) {
    // This plugin doesn't actually modify data, just visualizes it
    // The execution is essentially a no-op since the visualization happens in the preview
    return {
      success: true,
      itemsProcessed: 0,
      itemsSucceeded: 0,
      itemsFailed: 0,
    };
  },
};
